import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  GraduationCap, Search, Plus, Edit2, Trash2, Download,
  X, Loader2, ChevronDown, ChevronRight, Users, UserCheck, BookOpen, Upload, UserX,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import api from '../../api';
import { SCHOOL } from '../../constants/testIds';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

export default function TeachersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [teachers, setTeachers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editTeacher, setEditTeacher] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, male: 0, female: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ search, limit: 100 });
      if (genderFilter && genderFilter !== 'all') params.append('gender', genderFilter);
      const res = await api.get(`/teachers?${params}`);
      let data = res.data || [];
      if (genderFilter && genderFilter !== 'all') data = data.filter(t => t.gender === genderFilter);
      setTeachers(data);
      setTotal(res.total || data.length);
    } catch { toast.error('Failed to load teachers'); }
    finally { setLoading(false); }
  }, [search, genderFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/teachers/stats').then(r => { if (r.success) setStats(r.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId) {
      api.get(`/teachers/${editId}`).then(r => { if (r.success) setEditTeacher(r.data); }).catch(() => {});
    }
  }, [searchParams]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/teachers/${deleteId}`);
      toast.success('Teacher removed');
      setDeleteId(null);
      load();
      api.get('/teachers/stats').then(r => { if (r.success) setStats(r.data); }).catch(() => {});
    } catch { toast.error('Failed to remove teacher'); }
  };

  const handleExportCSV = () => {
    const headers = ['employee_number', 'first_name', 'last_name', 'email', 'phone', 'gender', 'qualification', 'employment_type', 'status'];
    const rows = teachers.map(t => headers.map(h => `"${(t[h] || '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const a = document.createElement('a');
    a.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    a.download = 'teachers.csv';
    a.click();
  };

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-5" data-testid="teachers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Teachers</h1>
          <p className="text-sm text-slate-500 mt-0.5">{stats.total} total on staff</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={handleExportCSV}
            className="flex items-center gap-1.5 border border-slate-200 text-slate-600 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-slate-50 transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={() => setShowImportModal(true)}
            className="flex items-center gap-1.5 border border-indigo-300 text-indigo-600 bg-indigo-50 text-sm font-semibold px-3 py-2 rounded-xl hover:bg-indigo-100 transition-colors">
            <BookOpen className="w-4 h-4" /> Bulk Import
          </button>
          <button onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors"
            data-testid={SCHOOL.addTeacherBtn}>
            <Plus className="w-4 h-4" /> Add Teacher
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Teachers', value: stats.total, icon: Users, color: 'indigo' },
          { label: 'Active', value: stats.active, icon: UserCheck, color: 'emerald' },
          { label: 'Male', value: stats.male, icon: GraduationCap, color: 'blue' },
          { label: 'Female', value: stats.female, icon: GraduationCap, color: 'violet' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-2 bg-${s.color}-100`}>
              <s.icon className={`w-4 h-4 text-${s.color}-600`} />
            </div>
            <p className="text-2xl font-extrabold text-slate-900">{s.value}</p>
            <p className="text-xs text-slate-500 font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Search + Filter */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full h-10 pl-9 pr-9 text-sm border border-slate-200 rounded-xl outline-none focus:border-indigo-400 transition-colors"
            data-testid="teacher-search" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="relative">
          <select value={genderFilter} onChange={e => setGenderFilter(e.target.value)}
            className="h-10 pl-3 pr-8 text-sm border border-slate-200 rounded-xl bg-white outline-none focus:border-indigo-400 appearance-none min-w-[140px]"
            data-testid="gender-filter">
            <option value="all">All Genders</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid={SCHOOL.teachersList}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 text-indigo-600 animate-spin" />
          </div>
        ) : teachers.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-slate-400" />
            </div>
            <p className="font-bold text-slate-700">No teachers found</p>
            <p className="text-sm text-slate-400 mt-1">{search ? 'Try a different search' : 'Add your first teacher to get started'}</p>
            {!search && (
              <button onClick={() => setShowAddModal(true)}
                className="mt-4 flex items-center gap-1.5 bg-indigo-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-indigo-700 mx-auto transition-colors">
                <Plus className="w-4 h-4" /> Add Teacher
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left px-5 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Teacher</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden md:table-cell">Emp. No.</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden sm:table-cell">Subjects</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide hidden lg:table-cell">Gender</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((t) => (
                  <tr key={t.id}
                    onClick={() => navigate(`/school/teachers/${t.id}`)}
                    className="border-b border-slate-50 hover:bg-indigo-50/30 cursor-pointer transition-colors group"
                    data-testid={`teacher-row-${t.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        {t.avatar ? (
                          <img src={t.avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-indigo-100" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center flex-shrink-0 text-white text-xs font-extrabold">
                            {t.first_name?.[0]}{t.last_name?.[0]}
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-bold text-slate-800 group-hover:text-indigo-700 transition-colors">{t.first_name} {t.last_name}</p>
                          <p className="text-xs text-slate-400">{t.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-500 hidden md:table-cell font-mono">{t.employee_number}</td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(t.subjects || []).slice(0, 2).map(s => (
                          <span key={s} className="text-[10px] font-semibold bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-md">{s}</span>
                        ))}
                        {(t.subjects || []).length > 2 && (
                          <span className="text-[10px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-md">+{t.subjects.length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 hidden lg:table-cell">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.gender === 'Male' ? 'bg-blue-50 text-blue-600' : 'bg-pink-50 text-pink-600'}`}>
                        {t.gender}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${t.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {t.employment_type || 'Full-time'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={e => { e.stopPropagation(); setEditTeacher(t); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          data-testid={`edit-teacher-${t.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); setDeleteId(t.id); }}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`delete-teacher-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 ml-1 transition-colors" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-slate-50 text-xs text-slate-400">
              Showing {teachers.length} of {total} teachers
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showImportModal && (
        <TeacherBulkImportModal onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false); load();
            api.get('/teachers/stats').then(r => { if (r.success) setStats(r.data); }).catch(() => {});
          }} />
      )}
      {showAddModal && (
        <TeacherModal teacher={null} onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false); load();
            api.get('/teachers/stats').then(r => { if (r.success) setStats(r.data); }).catch(() => {});
          }} />
      )}
      {editTeacher && (
        <TeacherModal teacher={editTeacher}
          onClose={() => { setEditTeacher(null); setSearchParams({}); }}
          onSuccess={() => { setEditTeacher(null); setSearchParams({}); load(); }} />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Teacher?</AlertDialogTitle>
            <AlertDialogDescription>This teacher will be marked as inactive. Their records will be preserved.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─── Teacher Add/Edit Modal ────────────────────────────────────── */
function TeacherModal({ teacher, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [subjectInput, setSubjectInput] = useState('');
  const photoRef = React.useRef(null);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', gender: 'Female',
    qualification: '', employment_type: 'Full-time', subjects: [], address: '',
    join_date: '', create_login: true, avatar: null,
    ...(teacher || {}),
  });
  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5MB'); return; }
    setUploadingPhoto(true);
    try {
      const url = await uploadToCloudinary(file, 'eduva/teachers');
      upd('avatar', url);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addSubject = () => {
    if (subjectInput.trim() && !form.subjects.includes(subjectInput.trim())) {
      upd('subjects', [...form.subjects, subjectInput.trim()]);
      setSubjectInput('');
    }
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('First name, last name and email are required'); return;
    }
    setSaving(true);
    try {
      if (teacher?.id) {
        await api.put(`/teachers/${teacher.id}`, form);
        toast.success('Teacher updated');
      } else {
        await api.post('/teachers', form);
        toast.success(`Teacher added! Login: ${form.email} / phone number`, { duration: 5000 });
      }
      onSuccess();
    } catch (err) { toast.error(err?.detail || 'Operation failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg">{teacher ? 'Edit Teacher' : 'Add New Teacher'}</DialogTitle>
        </DialogHeader>

        {/* Photo Upload */}
        <div className="flex items-center gap-4 pb-3 border-b border-slate-100">
          <div className="relative flex-shrink-0">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 border-2 border-indigo-100 overflow-hidden flex items-center justify-center">
              {form.avatar
                ? <img src={form.avatar} alt="Teacher" className="w-full h-full object-cover" />
                : <span className="text-xl font-bold text-indigo-300">
                    {form.first_name?.[0]?.toUpperCase() || <GraduationCap className="w-7 h-7" />}
                  </span>
              }
            </div>
            <button
              onClick={() => !uploadingPhoto && photoRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center shadow-md hover:bg-indigo-700 disabled:opacity-60"
              data-testid="teacher-upload-photo-btn">
              {uploadingPhoto ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            </button>
          </div>
          <div>
            <p className="font-semibold text-slate-700 text-sm">Teacher Photo</p>
            <p className="text-xs text-slate-400 mt-0.5">{uploadingPhoto ? 'Uploading...' : 'Click icon to upload (max 5MB)'}</p>
            {form.avatar && !uploadingPhoto && (
              <button onClick={() => upd('avatar', null)} className="text-xs text-red-500 hover:underline mt-0.5">Remove</button>
            )}
          </div>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} data-testid="teacher-photo-input" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {[
            { k: 'first_name', l: 'First Name *', ph: 'Adaeze' },
            { k: 'last_name', l: 'Last Name *', ph: 'Nwachukwu' },
            { k: 'email', l: 'Email *', ph: 'teacher@school.edu.ng' },
            { k: 'phone', l: 'Phone (Login Password)', ph: '+234 803...' },
          ].map(f => (
            <div key={f.k}>
              <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">{f.l}</Label>
              <Input type={f.k === 'email' ? 'email' : 'text'} value={form[f.k]} onChange={e => upd(f.k, e.target.value)} placeholder={f.ph} className="h-9 text-sm" />
            </div>
          ))}

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Gender</Label>
            <Select value={form.gender} onValueChange={v => upd('gender', v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Employment Type</Label>
            <Select value={form.employment_type} onValueChange={v => upd('employment_type', v)}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Full-time">Full-time</SelectItem>
                <SelectItem value="Part-time">Part-time</SelectItem>
                <SelectItem value="Contract">Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Qualification</Label>
            <Input value={form.qualification} onChange={e => upd('qualification', e.target.value)} placeholder="B.Ed, M.Sc..." className="h-9 text-sm" />
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Join Date</Label>
            <Input type="date" value={form.join_date} onChange={e => upd('join_date', e.target.value)} className="h-9 text-sm" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Home Address</Label>
            <Input value={form.address} onChange={e => upd('address', e.target.value)} placeholder="Home address" className="h-9 text-sm" />
          </div>

          <div className="col-span-2">
            <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Subjects</Label>
            <div className="flex gap-2">
              <Input value={subjectInput} onChange={e => setSubjectInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSubject())}
                placeholder="Add subject and press Enter..." className="h-9 text-sm flex-1" />
              <Button type="button" variant="outline" onClick={addSubject} className="h-9 px-3 text-sm">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {form.subjects.map(s => (
                <span key={s} onClick={() => upd('subjects', form.subjects.filter(x => x !== s))}
                  className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2.5 py-0.5 rounded-full cursor-pointer hover:bg-red-100 hover:text-red-600 transition-colors">
                  {s} ×
                </span>
              ))}
            </div>
          </div>

          {!teacher && (
            <div className="col-span-2 bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-xs text-indigo-600">
              <span className="font-bold">Login account will be created: </span>
              Email as username, phone number as default password
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving...</> : teacher ? 'Update Teacher' : 'Add Teacher'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Teacher Bulk Import Modal ─────────────────────────────────── */
function TeacherBulkImportModal({ onClose, onSuccess }) {
  const [step, setStep] = useState('upload'); // upload | preview | result
  const [parsedRows, setParsedRows] = useState([]);
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = React.useRef();

  const TEMPLATE = 'first_name,last_name,email,phone,gender,qualification,employment_type,subjects,address,join_date\nAdaeze,Nwachukwu,teacher6@school.edu.ng,08031234567,Female,B.Ed,Full-time,"Mathematics,Science",12 Lagos St,2024-01-15';

  const downloadTemplate = () => {
    const a = document.createElement('a');
    a.href = `data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE)}`;
    a.download = 'teachers_import_template.csv';
    a.click();
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    return lines.slice(1).map(line => {
      const vals = line.match(/(".*?"|[^,]+|(?<=,)(?=,)|(?<=,)$|^(?=,))/g) || [];
      const obj = {};
      headers.forEach((h, i) => { obj[h] = (vals[i] || '').replace(/^"|"$/g, '').trim(); });
      return obj;
    }).filter(r => r.first_name || r.last_name);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const rows = parseCSV(ev.target.result);
      if (!rows.length) { toast.error('No valid rows found in CSV'); return; }
      setParsedRows(rows);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.post('/teachers/bulk-import', { teachers_data: parsedRows });
      setResult(res.data);
      setStep('result');
      if (res.data?.imported > 0) toast.success(`${res.data.imported} teachers imported successfully!`);
    } catch (err) { toast.error(err?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-lg">Bulk Import Teachers</DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm text-indigo-700">
              <p className="font-semibold mb-1">Import Instructions</p>
              <ul className="text-xs space-y-0.5 text-indigo-600 list-disc ml-4">
                <li>Upload a CSV file with teacher data</li>
                <li>Required: first_name, last_name, email</li>
                <li>Add a <strong>phone</strong> column — used as default login password</li>
                <li>subjects column: comma-separated e.g. "Mathematics,Science"</li>
                <li>Login: email as username, phone as default password</li>
              </ul>
            </div>

            <button onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-indigo-300 rounded-xl p-3 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors">
              <Download className="w-4 h-4" /> Download CSV Template
            </button>

            <div
              className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
              onClick={() => fileRef.current?.click()}>
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm font-semibold text-slate-600">Click to upload CSV file</p>
              <p className="text-xs text-slate-400 mt-1">or drag and drop</p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} data-testid="teacher-import-file-input" />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-sm text-emerald-700">
              <p className="font-semibold">{parsedRows.length} teachers found in file</p>
              <p className="text-xs mt-0.5">{parsedRows.filter(r => r.phone).length} will have login accounts created</p>
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-100 max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-slate-500">Name</th>
                    <th className="text-left px-3 py-2 font-bold text-slate-500">Email</th>
                    <th className="text-left px-3 py-2 font-bold text-slate-500">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.map((r, i) => (
                    <tr key={i} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                      <td className="px-3 py-1.5 font-semibold">{r.first_name} {r.last_name}</td>
                      <td className="px-3 py-1.5 text-slate-500">{r.email}</td>
                      <td className="px-3 py-1.5 text-slate-500">{r.phone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('upload')} className="flex-1">Back</Button>
              <Button onClick={handleImport} disabled={importing} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
                {importing ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Importing...</> : `Import ${parsedRows.length} Teachers`}
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-4 text-center">
            <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center ${result.imported > 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              {result.imported > 0
                ? <GraduationCap className="w-8 h-8 text-green-600" />
                : <UserX className="w-8 h-8 text-red-600" />
              }
            </div>
            <div>
              <p className="text-xl font-extrabold text-slate-900">{result.imported} Imported</p>
              {result.accounts_created > 0 && <p className="text-sm text-indigo-600 font-semibold mt-1">{result.accounts_created} login accounts created</p>}
              {result.failed > 0 && <p className="text-sm text-red-600 font-semibold">{result.failed} failed</p>}
            </div>
            {result.errors?.length > 0 && (
              <div className="text-left bg-red-50 rounded-xl p-3 text-xs text-red-600 space-y-1 max-h-32 overflow-y-auto">
                {result.errors.map((e, i) => <p key={i}>• {e}</p>)}
              </div>
            )}
            <Button onClick={result.imported > 0 ? onSuccess : onClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
              {result.imported > 0 ? 'Done' : 'Close'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
